import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import {
  DOMAIN_CACHE_KEYS,
  DOMAIN_CACHE_PREFIXES,
  DOMAIN_CACHE_TTL_MS
} from './domain-cache.keys';

interface CacheEntry {
  value$: Observable<unknown>;
  expiresAt: number;
}

/**
 * Cache de sessão para listas de domínio (combos).
 * Expira após TTL; invalida no write do domínio e no logout.
 */
@Injectable({
  providedIn: 'root'
})
export class DomainCacheService {
  private readonly cache = new Map<string, CacheEntry>();

  getOrLoad<T>(key: string, loader: () => Observable<T>): Observable<T> {
    const existing = this.cache.get(key);
    if (existing && existing.expiresAt > Date.now()) {
      return existing.value$ as Observable<T>;
    }

    if (existing) {
      this.cache.delete(key);
    }

    const request$ = loader().pipe(
      tap({
        error: () => this.cache.delete(key)
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.cache.set(key, {
      value$: request$,
      expiresAt: Date.now() + DOMAIN_CACHE_TTL_MS
    });
    return request$;
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  invalidateKeys(keys: string[]): void {
    keys.forEach((key) => this.invalidate(key));
  }

  invalidateByPrefix(prefix: string): void {
    for (const key of [...this.cache.keys()]) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  invalidateDominio(dominio: string): void {
    const normalized = (dominio || '').toUpperCase();
    this.invalidateKeys(DOMAIN_CACHE_KEYS[normalized] || []);
    (DOMAIN_CACHE_PREFIXES[normalized] || []).forEach((prefix) => this.invalidateByPrefix(prefix));
  }

  clear(): void {
    this.cache.clear();
  }
}
